import type { FunctionComponent, PropsWithChildren } from 'react'
import React, { useEffect, useState } from 'react'
import { useContext } from 'react'
import { Button, Link, TypographyV2 as Typography } from '@which/seatbelt'

import { salesforceMapper } from '../../../tools/BookAppointment/PageComponents/AppointmentChoice/salesforceMapper'
import { ToolContext } from '../../state/appContext'
import { ImageWithQuestion } from '../ImageWithQuestion'
import { Loader } from '../Loader'
import { MyAppointment } from '../MyAppointment'
import styles from './CheckSimilarAppointment.module.scss'
import { similarCaseData } from './data'
import type { SimilarCaseMatch } from './fetchSimilarCase'
import { fetchSimilarCase } from './fetchSimilarCase'

export const CheckSimilarAppointment: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { state, dispatch } = useContext(ToolContext)

  const {
    buying,
    legal,
    employment,
    holiday,
    other,
    home,
    triage,
    money,
    cars,
    tech,
    wills: { willsTopic, willsQuery },
    initial: { user },
  } = state

  const [similarCaseMatch, setSimilarCaseMatch] = useState<SimilarCaseMatch | null>(null)

  const { imgObj, title, standFirst } = similarCaseData
  const { caseType } = salesforceMapper({
    buying,
    legal,
    employment,
    holiday,
    other,
    home,
    money,
    triage,
    cars,
    tech,
    initial: { user },
    wills: { willsTopic, willsQuery },
  })

  const isSkipped = triage.skipSimilarAppointment === caseType

  useEffect(() => {
    if (similarCaseMatch === null && !isSkipped) {
      const createIFrame = async () => {
        const iFrameResponse = await fetchSimilarCase(caseType)
        setSimilarCaseMatch(iFrameResponse)
      }
      createIFrame()
    }
  }, [caseType, isSkipped, similarCaseMatch])

  if (isSkipped || (similarCaseMatch && !similarCaseMatch.foundSimilarCase)) {
    return <>{children}</>
  }

  if (similarCaseMatch === null) {
    return <Loader />
  }

  return (
    <div>
      <ImageWithQuestion image={imgObj} title={title} />
      <p className={styles.checkSimilarAppointmentStandFirst} data-testid="typography-body">
        {standFirst}
      </p>
      <Typography
        className={styles.checkSimilarAppointmentCenter}
        data-testid="typography-extra"
        textStyle="sb-text-body-default-regular"
      >
        You can <strong>cancel or reschedule</strong> your appointment below or in{' '}
        <Link href="/my-appointments">My Appointments.</Link>
      </Typography>
      <div className={styles.checkSimilarAppointmentContainer}>
        <MyAppointment
          buttonLink={`/tool/change-appointment?appointmentId=${similarCaseMatch.similarCase.appointment.id}`}
          buttonText="Cancel or reschedule"
          caseView={similarCaseMatch.similarCase}
        />
        <Typography
          className={styles.checkSimilarAppointmentContinue}
          textStyle="sb-text-interface-body-default-strong"
        >
          Still have a new, unrelated issue?
        </Typography>
        <Button
          appearance="secondary"
          className={styles.checkSimilarAppointmentContinueButton}
          data-testid="appointment-continue"
          onClick={() => {
            dispatch({ type: 'UPDATE_TRIAGE_SKIP_SIMILAR_APPOINTMENT', data: caseType })
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
